import 'es6-promise/auto';
import * as fetch from 'isomorphic-fetch';
import { UserData } from './userdata/types';
import { unpackWorkspace } from './userdata/unpacker';
import { packWorkspace } from './userdata/packer';
import ViewerInstanceBase from './ViewerInstanceBase';
import { setDevelopMode, setLegacyUI } from './actions/AppAction';
import { createUserdataFromPartialWorkspaceString } from './userdata/partial';
import { isFullscreen } from '../../../common/src/ts/utils/fullscreen';
import { BrowserComposerBridge } from './composer';

class ViewerInstance extends ViewerInstanceBase {
  private attachments: { [id: string]: Blob };

  protected composerBridge = new BrowserComposerBridge();

  protected reactWillRenderApp() {
    const devModeMatches = /[\?&]dev=(true)/.exec(location.search);
    this.dispatchDevModeIfNeeded(devModeMatches);
    this.setDevModeBackgroundIfNeeded(devModeMatches);
  }

  private dispatchDevModeIfNeeded(devModeMatches: RegExpExecArray | null) {
    if (devModeMatches !== null && devModeMatches[1] === 'true') {
      this.store.dispatch(setDevelopMode(true));
    }

    const legacyUIMatches = /[\?&]legacyUI=(true)/.exec(location.search);
    if (legacyUIMatches !== null && legacyUIMatches[1] === 'true') {
      this.store.dispatch(setLegacyUI(true));
    }
  }

  private setDevModeBackgroundIfNeeded(devModeMatches: RegExpExecArray | null) {
    if (devModeMatches === null || devModeMatches[1] !== 'true') {
      return;
    }

    const internalUseOnly = document.createElement('style');
    const cssText = [
      'content: "";',
      'position: absolute;',
      'width: 200%;',
      'height: 200%;',
      'top: -50%;',
      'left: -50%;',
      'background-image: url("images/internal-use-only.svg");',
      'z-index: -1;',
      'opacity: 0.025;',
      'transform: rotate(-30deg);',
    ].join(' ');
    const styleSheet = document.createTextNode(`body::before { ${cssText} }`);
    internalUseOnly.appendChild(styleSheet);
    document.body.appendChild(internalUseOnly);
  }

  private baseDir() {
    const { protocol, host, pathname } = window.location;
    const dir = pathname.substr(0, pathname.lastIndexOf('/'));
    return `${protocol}//${host}${dir}/files/`;
  }

  private metadataDir() {
    return `${this.baseDir()}.ark/`;
  }

  private readingUnitDir(location: string) {
    return `${this.metadataDir()}reading-units/${location}/`;
  }

  protected readIndex() {
    const opts: RequestInit = { credentials: 'same-origin' };
    return fetch(`${this.metadataDir()}index.json`, opts).then(res => res.json());
  }

  protected readPublicationData(location: string) {
    const opts: RequestInit = { credentials: 'same-origin' };
    return fetch(`${this.readingUnitDir(location)}publication.json`, opts).then(res => res.json());
  }

  protected resourceDir(location: string) {
    // Trailing slash is not assumed by things using this path as string.
    return `${this.baseDir()}${location}`;
  }

  protected openBookshelf() {
    window.location.href = 'index.html';
  }

  protected openFileDialog(_cb: (filePath: string) => void) {
    alert('この機能はアプリ版で使用できます');
  }

  protected openFile(_filePath: string) {
    alert('この機能はアプリ版で使用できます');
  }

  protected readAttachment(id: string) {
    return new Promise<Blob>((resolve, reject) => {
      if (id in this.attachments) {
        resolve(this.attachments[id]);
        return;
      }

      reject(new Error('Illegal state.'));
    });
  }

  protected writeAttachment(id: string, blob: Blob) {
    return new Promise<void>((resolve) => {
      this.attachments[id] = blob;
      resolve();
    });
  }

  protected saveWorkspace(main: any, attachments: ReadonlyArray<string>, fileName: string) {
    const retainedAttachments: { [id: string]: true } = {};
    for (const id of attachments) {
      retainedAttachments[id] = true;
    }

    for (const id in this.attachments) {
      if (!(id in retainedAttachments)) {
        delete this.attachments[id];
      }
    }

    // TODO : Now packWorkspace can be synchronous function.
    return packWorkspace(main, this.attachments, fileName);
  }

  protected toggleFullScreen() {
    if (isFullscreen()) {
      if (document.exitFullscreen !== undefined) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen !== undefined) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen !== undefined) {
        (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen !== undefined) {
        (document as any).msExitFullscreen();
      }
    } else {
      const el = document.body;
      if (el.requestFullscreen !== undefined) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullScreen !== undefined) {
        el.webkitRequestFullScreen();
      } else if ((el as any).mozRequestFullScreen !== undefined) { // FIXME: define type
        (el as any).mozRequestFullScreen();
      } else if ((el as any).msRequestFullscreen !== undefined) {
        (el as any).msRequestFullscreen();
      }
    }
  }

  public constructor(
    contentID: string,
    componentID: string | null,
    workspace: UserData | null = null,
    attachments: { readonly [id: string]: Blob } = {},
  ) {
    super(contentID, componentID, workspace);
    this.attachments = { ...attachments };
    this.start();
  }
}

window.addEventListener('load', () => {
  const readUserData = (file: File) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const unpacked = unpackWorkspace(reader.result as ArrayBuffer);
      const mainData = unpacked.main as UserData;

      const attachments: { [id: string]: Blob } = {};
      for (const attachment of unpacked.attachments) {
        attachments[attachment.filename] = new Blob([attachment.data]);
      }

      new ViewerInstance(mainData.publicationId, null, mainData, attachments);
    };
    reader.readAsArrayBuffer(file);
  };

  const readWorkspaceFromParam = (hexData: string) => {
    let unpacked;
    try {
      unpacked = createUserdataFromPartialWorkspaceString(hexData);
    } catch (e) {
      console.error(e);
      alert('パラメータが間違っています');
      return;
    }
    new ViewerInstance(unpacked.publicationId, null, unpacked, {});
  };

  const contentMatches = /[\?&]contentId=([\w-_%\.~]+)/.exec(location.search);
  const componentMatches = /[\?&]component=([\w-_%\.~]+)/.exec(location.search);
  const paramsMatches = /[\?&]workspaceData=([0-9a-fA-F]+)/.exec(location.search);

  if (contentMatches !== null) {
    new ViewerInstance(
      decodeURIComponent(contentMatches[1]),
      componentMatches === null ? null : decodeURIComponent(componentMatches[1]),
    );
  } else if (paramsMatches !== null) {
    readWorkspaceFromParam(paramsMatches[1]);
  } else {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = () => {
      const files = input.files;
      if (files !== null && files.length > 0) {
        readUserData(files[0]);
      }
    };

    const container = document.getElementById('react-root');
    if (container === null) {
      throw new Error('No root element found');
    }
    container.appendChild(document.createTextNode('学習履歴ファイルを選択:'));
    container.appendChild(input);
  }
});

// Workaround for iOS
//
// iOS環境でbodyをfixedで固定していてもドラッグ動作によりスクロールが発生し
// 紙面のパニングやペンでの書き込み等が正しく行えない問題への対策
// iOS 11.3 よりbody、document、windowへのtouchstartとtouchmoveは既定でpassive: trueのため
// passive: false を明示する
//
// See:
//   https://github.com/atlassian/react-beautiful-dnd/issues/413
//   https://bugs.webkit.org/show_bug.cgi?id=182521#c3
window.addEventListener(
  'touchmove',
  (ev: TouchEvent) => { ev.preventDefault(); },
  { passive: false, capture: false },
);
